import { Controller } from '@hotwired/stimulus';

// Dépôt des PDF d'un DOE : fichiers choisis, dossier entier (sous-dossiers
// compris) ou glissés dans la zone ; seuls les PDF sont gardés (les autres
// comptés), sans doublon ; liste avec taille et retrait, total ; tout est
// remis dans le champ du formulaire (cible input). Envoi bloqué sans fichier
// ou au-delà du nombre et des tailles permis (le serveur reste seul juge).
export default class extends Controller {
    static targets = ['input', 'folder', 'zone', 'list', 'summary', 'total', 'error'];

    static values = { maxFile: Number, maxTotal: Number, maxCount: Number };

    connect() {
        this.files = [];
        this.ignored = 0;
    }

    pick() {
        this.add(Array.from(this.inputTarget.files));
    }

    pickFolder() {
        this.add(Array.from(this.folderTarget.files));
        this.folderTarget.value = '';
    }

    over(event) {
        event.preventDefault();
        this.zoneTarget.classList.add('border-accent');
    }

    leave() {
        this.zoneTarget.classList.remove('border-accent');
    }

    async drop(event) {
        event.preventDefault();
        this.leave();
        const entries = Array.from(event.dataTransfer.items || []).map((item) => item.webkitGetAsEntry?.()).filter(Boolean);
        if (entries.length === 0) {
            this.add(Array.from(event.dataTransfer.files));
            return;
        }
        const files = await Promise.all(entries.map((entry) => this.read(entry)));
        this.add(files.flat());
    }

    remove({ params: { index } }) {
        this.files.splice(index, 1);
        this.sync();
    }

    check(event) {
        if (!this.showProblem()) return;

        event.preventDefault();
        this.errorTarget.scrollIntoView({ block: 'nearest' });
    }

    showProblem() {
        const message = this.problem();
        this.errorTarget.textContent = message || '';
        this.errorTarget.hidden = !message;
        return message;
    }

    // Fichiers d'une entrée glissée : le fichier, ou ceux du dossier et de
    // ses sous-dossiers.
    async read(entry) {
        if (entry.isFile) return [await new Promise((resolve, reject) => entry.file(resolve, reject))];

        const reader = entry.createReader();
        const children = [];
        let batch;
        do {
            batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
            children.push(...batch);
        } while (batch.length > 0);
        return (await Promise.all(children.map((child) => this.read(child)))).flat();
    }

    add(files) {
        files.forEach((file) => {
            if (!this.isPdf(file)) {
                this.ignored += 1;
                return;
            }
            if (!this.files.some((kept) => kept.name === file.name && kept.size === file.size)) this.files.push(file);
        });
        this.sync();
    }

    isPdf(file) {
        return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    }

    sync() {
        const transfer = new DataTransfer();
        this.files.forEach((file) => transfer.items.add(file));
        this.inputTarget.files = transfer.files;
        this.render();
    }

    render() {
        this.listTarget.replaceChildren(...this.files.map((file, index) => this.row(file, index)));
        this.summaryTarget.hidden = this.files.length === 0;
        const total = this.files.reduce((sum, file) => sum + file.size, 0);
        const count = `${this.files.length} PDF, ${this.size(total)}`;
        const ignored = this.ignored > 0 ? ` ; ${this.ignored} fichier${this.ignored > 1 ? 's' : ''} non PDF ignoré${this.ignored > 1 ? 's' : ''}` : '';
        this.totalTarget.textContent = `${count}${ignored}.`;
        if (!this.errorTarget.hidden) this.showProblem();
    }

    row(file, index) {
        const item = document.createElement('li');
        item.className = 'flex items-center gap-3 px-3 py-2';
        const name = document.createElement('span');
        name.className = 'min-w-0 flex-1 truncate';
        name.textContent = file.webkitRelativePath || file.name;
        const size = document.createElement('span');
        size.className = `tabular-nums ${file.size > this.maxFileValue ? 'text-ko' : 'text-muted'}`;
        size.textContent = this.size(file.size);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'text-sm text-accent hover:underline';
        button.textContent = 'Retirer';
        button.setAttribute('aria-label', `Retirer ${file.name}`);
        button.dataset.action = 'doe-deposit#remove';
        button.dataset.doeDepositIndexParam = String(index);
        item.append(name, size, button);
        return item;
    }

    problem() {
        if (this.files.length === 0) return 'Déposez au moins un PDF.';
        if (this.files.length > this.maxCountValue) return `${this.maxCountValue} PDF au plus par lecture : déposez le DOE en plusieurs lectures.`;
        const big = this.files.filter((file) => file.size > this.maxFileValue).map((file) => file.name);
        if (big.length > 0) return `150 Mo au plus par PDF : ${big.join(', ')}.`;
        const total = this.files.reduce((sum, file) => sum + file.size, 0);
        if (total > this.maxTotalValue) return '500 Mo au plus par lecture.';
        return null;
    }

    size(bytes) {
        return `${(bytes / 1048576).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
    }
}
